import { Button, Col, Row, Text } from 'components';
import { getModerationPolicy, theme, updateModerationPolicy } from 'lib';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  CATEGORY_LABELS,
  MODERATION_CATEGORIES,
  SEVERITY_LEVELS,
  THRESHOLD_OPTIONS
} from '../constants';
import { AccessDenied, ErrorState, LoadingBlock, Section, ToggleHeader } from './ui';

const THRESHOLD_KEYS = [
  { key: 'warnAt', label: 'Warn at' },
  { key: 'flagAt', label: 'Flag at' },
  { key: 'blockAt', label: 'Block at' }
];

const StyledSelect = styled.select`
  height: 34px;
  border: 1px solid ${theme.GREY_20};
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  font-family: CeraPro;
  color: ${theme.GREY_80};
  background: #fff;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: ${theme.SECONDARY};
  }
`;

/** null <-> 'off' so a native select can carry the "disabled" threshold. */
const toSelectValue = (v) => (v === null || v === undefined ? 'off' : String(v));
const fromSelectValue = (v) => (v === 'off' ? null : Number(v));

const thresholdEqual = (a, b) =>
  a.enabled === b.enabled && a.warnAt === b.warnAt && a.flagAt === b.flagAt && a.blockAt === b.blockAt;

/** warnAt <= flagAt <= blockAt among the thresholds that are set (non-null). */
const validateThresholds = (t) => {
  const order = [t.warnAt, t.flagAt, t.blockAt];
  const labels = ['Warn', 'Flag', 'Block'];
  let prevVal = null;
  let prevLabel = null;
  for (let i = 0; i < order.length; i++) {
    const v = order[i];
    if (v == null) continue;
    if (prevVal != null && v < prevVal) {
      return `${prevLabel} at ${prevVal} is stricter than ${labels[i]} at ${v}. Keep Warn ≤ Flag ≤ Block.`;
    }
    prevVal = v;
    prevLabel = labels[i];
  }
  return null;
};

const ThresholdSelect = ({ label, value, onChange }) => (
  <Col noFlex style={{ flex: '1 1 120px', minWidth: '110px' }}>
    <Text c1 col={theme.GREY_60} marg="0 0 5px" style={{ fontSize: '11px', fontWeight: 600 }}>
      {label}
    </Text>
    <StyledSelect value={toSelectValue(value)} onChange={(e) => onChange(fromSelectValue(e.target.value))}>
      {THRESHOLD_OPTIONS.map((opt) => (
        <option key={opt.label} value={toSelectValue(opt.value)}>
          {opt.label}
        </option>
      ))}
    </StyledSelect>
  </Col>
);

const Legend = () => (
  <Row center noFlex gap="12px" wrapFlex marg="0 0 4px">
    {SEVERITY_LEVELS.map((lvl) => (
      <Row center noFlex gap="6px" key={lvl.value}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background:
              lvl.value >= 6
                ? '#E11D48'
                : lvl.value >= 4
                  ? '#EA580C'
                  : lvl.value >= 2
                    ? '#CA8A04'
                    : '#16A34A'
          }}
        />
        <Text c1 col={theme.GREY_60} style={{ fontSize: '11.5px' }}>
          {lvl.value} {lvl.label.toLowerCase()}
        </Text>
      </Row>
    ))}
  </Row>
);

const NumberField = ({ label, value, onChange, min = 1, suffix }) => (
  <Col noFlex style={{ flex: '1 1 140px', minWidth: '130px' }}>
    <Text c1 col={theme.GREY_60} marg="0 0 5px" style={{ fontSize: '11px', fontWeight: 600 }}>
      {label}
    </Text>
    <Row center noFlex gap="6px">
      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value) || min))}
        style={{
          height: '34px',
          width: '100%',
          border: `1px solid ${theme.GREY_20}`,
          borderRadius: '8px',
          padding: '0 10px',
          fontSize: '13px',
          fontFamily: 'CeraPro',
          color: theme.GREY_80
        }}
      />
      {suffix && (
        <Text c1 col={theme.GREY_50} style={{ fontSize: '11.5px', whiteSpace: 'nowrap' }}>
          {suffix}
        </Text>
      )}
    </Row>
  </Col>
);

const CategoryCard = ({ category, threshold, onChange }) => {
  const error = validateThresholds(threshold);
  return (
    <Col
      noFlex
      wid="100%"
      pad="16px 18px"
      style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: `1px solid ${error ? theme.SECONDARY_20 : theme.GREY_20}`
      }}
    >
      <ToggleHeader
        title={CATEGORY_LABELS[category]}
        description={threshold.enabled ? 'Actively moderated' : 'Not moderated'}
        on={threshold.enabled}
        onToggle={() => onChange({ ...threshold, enabled: !threshold.enabled })}
      />

      <Row
        start
        noFlex
        gap="12px"
        wrapFlex
        marg="14px 0 0"
        style={{ opacity: threshold.enabled ? 1 : 0.5 }}
      >
        {THRESHOLD_KEYS.map(({ key, label }) => (
          <ThresholdSelect
            key={key}
            label={label}
            value={threshold[key]}
            onChange={(v) => onChange({ ...threshold, [key]: v })}
          />
        ))}
      </Row>

      {error && (
        <Text c1 col={theme.SECONDARY} marg="10px 0 0" style={{ fontSize: '11.5px' }}>
          ⚠ {error}
        </Text>
      )}
    </Col>
  );
};

const PolicyTab = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [draft, setDraft] = useState(null);

  const fetchPolicy = async () => {
    setLoading(true);
    setError(null);
    const res = await getModerationPolicy();
    if (res?.status === 403) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (res?.err || !res?.data) {
      setError(res?.err || 'Could not load the moderation policy.');
      setLoading(false);
      return;
    }
    setBaseline(res.data);
    setDraft(res.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCategory = (category, threshold) =>
    setDraft((prev) => ({
      ...prev,
      categories: { ...prev.categories, [category]: threshold }
    }));

  const hasInvalid =
    draft && MODERATION_CATEGORIES.some((c) => validateThresholds(draft.categories[c]));

  const SPAM_NUMERIC_FIELDS = [
    'spamDuplicateThreshold',
    'spamDuplicateWindowMs',
    'spamRateThreshold',
    'spamRateWindowMs'
  ];

  const buildDiff = () => {
    const changed = {};
    if ((draft.profanityEnabled !== false) !== (baseline.profanityEnabled !== false)) {
      changed.profanityEnabled = draft.profanityEnabled !== false;
    }
    if ((draft.spamEnabled !== false) !== (baseline.spamEnabled !== false)) {
      changed.spamEnabled = draft.spamEnabled !== false;
    }
    SPAM_NUMERIC_FIELDS.forEach((key) => {
      if (draft[key] !== baseline[key]) changed[key] = draft[key];
    });
    const categories = {};
    MODERATION_CATEGORIES.forEach((c) => {
      const next = draft.categories[c];
      const prev = baseline.categories[c];
      if (!prev || !thresholdEqual(next, prev)) {
        // Send the full threshold object — the API requires all four fields.
        categories[c] = {
          enabled: next.enabled,
          warnAt: next.warnAt,
          flagAt: next.flagAt,
          blockAt: next.blockAt
        };
      }
    });
    if (Object.keys(categories).length) changed.categories = categories;
    return changed;
  };

  const handleSave = async () => {
    if (hasInvalid) return;
    const diff = buildDiff();
    if (!Object.keys(diff).length) {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'No changes to save.' } });
      return;
    }
    setSaving(true);
    const res = await updateModerationPolicy(diff);
    setSaving(false);
    if (res?.status === 403) {
      setForbidden(true);
      return;
    }
    if (res?.err || !res?.data) {
      dispatch({ type: 'HANDLE_RES', payload: { err: res?.err || 'Failed to update policy.' } });
      return;
    }
    // Refresh the form from the returned (authoritative) policy.
    setBaseline(res.data);
    setDraft(res.data);
    dispatch({ type: 'HANDLE_RES', payload: { res: res.msg || 'Moderation policy updated.' } });
  };

  const dirty = baseline && draft && Object.keys(buildDiff()).length > 0;

  if (forbidden) return <AccessDenied />;
  if (loading) return <LoadingBlock height="320px" />;
  if (error) return <ErrorState message={error} onRetry={fetchPolicy} />;
  if (!draft) return null;

  return (
    <Col noFlex wid="100%" gap="16px" style={{ maxWidth: '1240px' }}>
      <Section
        title="Categories"
        description="Per-category severity thresholds. For each harm category, choose the severity at which to Warn, Flag (hold for review), or Block."
      >
        <Legend />
        <Row grid numCols={2} gap="14px" wid="100%" marg="14px 0 0">
          {MODERATION_CATEGORIES.map((c) => (
            <CategoryCard
              key={c}
              category={c}
              threshold={draft.categories[c]}
              onChange={(t) => setCategory(c, t)}
            />
          ))}

          <Col
            noFlex
            wid="100%"
            pad="16px 18px"
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: `1px solid ${theme.GREY_20}`
            }}
          >
            <ToggleHeader
              title="Profanity"
              description={
                draft.profanityEnabled !== false ? 'Actively moderated' : 'Not moderated'
              }
              on={draft.profanityEnabled !== false}
              onToggle={() =>
                setDraft((p) => ({ ...p, profanityEnabled: !(p.profanityEnabled !== false) }))
              }
            />
            <Text c1 col={theme.GREY_60} marg="14px 0 0" style={{ fontSize: '11.5px' }}>
              Custom word list matched locally before Azure. Severe terms block instantly;
              manage the list in the Profanity tab.
            </Text>
          </Col>
        </Row>
      </Section>

      <Section
        title="Spam"
        description="Behavioral spam detection: duplicate-content blasts, request rate, and known spam-link domains. Manage the domain blocklist and hard-blocked business-rule words (e.g. crypto, bitcoin) in the Spam tab."
      >
        <Col
          noFlex
          wid="100%"
          pad="16px 18px"
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: `1px solid ${theme.GREY_20}`
          }}
        >
          <ToggleHeader
            title="Spam detection"
            description={draft.spamEnabled !== false ? 'Actively checked' : 'Not checked'}
            on={draft.spamEnabled !== false}
            onToggle={() => setDraft((p) => ({ ...p, spamEnabled: !(p.spamEnabled !== false) }))}
          />

          <Row
            start
            noFlex
            gap="12px"
            wrapFlex
            marg="16px 0 0"
            style={{ opacity: draft.spamEnabled !== false ? 1 : 0.5 }}
          >
            <NumberField
              label="Duplicate threshold"
              value={draft.spamDuplicateThreshold}
              min={2}
              suffix="repeats to block"
              onChange={(v) => setDraft((p) => ({ ...p, spamDuplicateThreshold: v }))}
            />
            <NumberField
              label="Duplicate window"
              value={Math.round(draft.spamDuplicateWindowMs / 60000)}
              min={1}
              suffix="minutes"
              onChange={(v) =>
                setDraft((p) => ({ ...p, spamDuplicateWindowMs: v * 60000 }))
              }
            />
            <NumberField
              label="Rate threshold"
              value={draft.spamRateThreshold}
              min={2}
              suffix="actions to block"
              onChange={(v) => setDraft((p) => ({ ...p, spamRateThreshold: v }))}
            />
            <NumberField
              label="Rate window"
              value={Math.round(draft.spamRateWindowMs / 1000)}
              min={5}
              suffix="seconds"
              onChange={(v) => setDraft((p) => ({ ...p, spamRateWindowMs: v * 1000 }))}
            />
          </Row>

          <Text c1 col={theme.GREY_60} marg="14px 0 0" style={{ fontSize: '11.5px' }}>
            Example: with the defaults, a user posting the same text 5 times within 10 minutes,
            or more than 10 guarded actions within 60 seconds (any content type), is blocked
            instantly.
          </Text>
        </Col>
      </Section>

      <Row center between noFlex wid="100%">
        <Text c1 col={theme.GREY_60} style={{ fontSize: '12px' }}>
          {hasInvalid
            ? 'Fix the threshold ordering above before saving.'
            : dirty
              ? 'You have unsaved changes.'
              : 'All changes saved.'}
        </Text>
        <Button
          text={saving ? 'Saving…' : 'Save changes'}
          onClick={handleSave}
          bg={theme.SECONDARY}
          loading={saving}
          disabled={saving || hasInvalid || !dirty}
          small
          wid="160px"
        />
      </Row>
    </Col>
  );
};

export default PolicyTab;
